function arrayLast<T>(array: T[]) {
  return array[array.length - 1];
}

export function average(array: number[] | undefined): number {
  return array && array.length > 0
    ? array.reduce((acc, val) => val + acc, 0) / array.length
    : 0;
}

export interface Point {
  x: number;
  y: number;
}

export const equals = (point1: Point, point2: Point): boolean => {
  return point1.x === point2.x && point1.y === point2.y;
};

export const add = (point1: Point, point2: Point): Point => {
  return {
    x: point1.x + point2.x,
    y: point1.y + point2.y,
  };
};

export const subtract = (point1: Point, point2: Point): Point => {
  return {
    x: point1.x - point2.x,
    y: point1.y - point2.y,
  };
};

export const magnitude = (point: Point): number => {
  return Math.sqrt(Math.pow(point.x, 2) + Math.pow(point.y, 2));
};

export const distance = (point1: Point, point2: Point): number => {
  return magnitude(subtract(point1, point2));
};

export const length = (points: Point[]): number => {
  const [head, ...tail] = points;
  let lastPoint = head as Point;
  return tail.reduce((acc, point) => {
    const dist = distance(point, lastPoint);
    lastPoint = point;
    return acc + dist;
  }, 0);
};

export const cosineSimilarity = (point1: Point, point2: Point): number => {
  const magnitude1 = magnitude(point1);
  const magnitude2 = magnitude(point2);
  if (magnitude1 === 0 || magnitude2 === 0) {
    return 0;
  }
  return (point1.x * point2.x + point1.y * point2.y) / magnitude1 / magnitude2;
};

/**
 * return a new point, p3, which is on the same line as p1 and p2, but distance away
 * from p2. p1, p2, p3 will always lie on the line in that order
 */
export const extendLine = (
  point1: Point,
  point2: Point,
  dist: number,
): Point => {
  const vect = subtract(point2, point1);
  const magni = magnitude(vect);
  const norm = magni === 0 ? 0 : dist / magni;
  return { x: point2.x + norm * vect.x, y: point2.y + norm * vect.y };
};

/** based on http://www.kr.tuwien.ac.at/staff/eiter/et-archive/cdtr9464.pdf */
export const frechetDist = (curve1: Point[], curve2: Point[]): number => {
  const longCurve = curve1.length >= curve2.length ? curve1 : curve2;
  const shortCurve = curve1.length >= curve2.length ? curve2 : curve1;

  const calcVal = (
    i: number,
    j: number,
    prevResultsCol: number[],
    curResultsCol: number[],
  ) => {
    if (i === 0 && j === 0) {
      return distance(longCurve[0] as Point, shortCurve[0] as Point);
    }

    if (i > 0 && j === 0) {
      return Math.max(
        prevResultsCol[0] as number,
        distance(longCurve[i] as Point, shortCurve[0] as Point),
      );
    }

    const lastResult = curResultsCol[curResultsCol.length - 1] as number;

    if (i === 0 && j > 0) {
      return Math.max(
        lastResult,
        distance(longCurve[0] as Point, shortCurve[j] as Point),
      );
    }

    return Math.max(
      Math.min(
        prevResultsCol[j] as number,
        prevResultsCol[j - 1] as number,
        lastResult,
      ),
      distance(longCurve[i] as Point, shortCurve[j] as Point),
    );
  };

  let prevResultsCol: number[] = [];
  for (let i = 0; i < longCurve.length; i++) {
    const curResultsCol: number[] = [];
    for (let j = 0; j < shortCurve.length; j++) {
      // we only need the results from i - 1 and j - 1 to continue the calculation
      // so we only need to hold onto the last column of calculated results
      // prevResultsCol is results[i-1][:] in the original algorithm
      // curResultsCol is results[i][:j-1] in the original algorithm
      curResultsCol.push(calcVal(i, j, prevResultsCol, curResultsCol));
    }
    prevResultsCol = curResultsCol;
  }

  return prevResultsCol[shortCurve.length - 1] as number; // FIXME: fix type
};

/** break up long segments in the curve into smaller segments of len maxLen or smaller */
export const subdivideCurve = (curve: Point[], maxLen = 0.05): Point[] => {
  const newCurve = curve.slice(0, 1);

  for (const point of curve.slice(1)) {
    const prevPoint = newCurve[newCurve.length - 1] as Point;
    const segLen = distance(point, prevPoint);
    if (segLen > maxLen) {
      const numNewPoints = Math.ceil(segLen / maxLen);
      const newSegLen = segLen / numNewPoints;
      for (let i = 0; i < numNewPoints; i++) {
        newCurve.push(extendLine(point, prevPoint, -1 * newSegLen * (i + 1)));
      }
    } else {
      newCurve.push(point);
    }
  }

  return newCurve;
};

/** redraw the curve using numPoints equally spaced out along the length of the curve */
export const outlineCurve = (curve: Point[], numPoints = 30): Point[] => {
  const curveLen = length(curve);
  const segmentLen = curveLen / (numPoints - 1);
  const outlinePoints = [curve[0] as Point];
  const endPoint = arrayLast(curve) as Point;
  const remainingCurvePoints = curve.slice(1);

  for (let i = 0; i < numPoints - 2; i++) {
    let lastPoint = arrayLast(outlinePoints);
    let remainingDist = segmentLen;
    let outlinePointFound = false;
    while (!outlinePointFound) {
      const nextPointDist = distance(
        lastPoint as Point,
        remainingCurvePoints[0] as Point,
      );
      if (nextPointDist < remainingDist) {
        remainingDist -= nextPointDist;
        lastPoint = remainingCurvePoints.shift();
      } else {
        const nextPoint = extendLine(
          lastPoint as Point,
          remainingCurvePoints[0] as Point,
          remainingDist - nextPointDist,
        );
        outlinePoints.push(nextPoint);
        outlinePointFound = true;
      }
    }
  }

  outlinePoints.push(endPoint);

  return outlinePoints;
};

/** translate and scale from https://en.wikipedia.org/wiki/Procrustes_analysis */
export const normalizeCurve = (curve: Point[]): Point[] => {
  const outlinedCurve = outlineCurve(curve);
  const meanX = average(outlinedCurve.map((point) => point.x));
  const meanY = average(outlinedCurve.map((point) => point.y));
  const mean = { x: meanX, y: meanY };
  const translatedCurve = outlinedCurve.map((point) => subtract(point, mean));
  const translatedFirst = translatedCurve[0] as Point;
  const translatedLast = arrayLast(translatedCurve) as Point;
  const scale = Math.sqrt(
    average([
      Math.pow(translatedFirst.x, 2) + Math.pow(translatedFirst.y, 2),
      Math.pow(translatedLast.x, 2) + Math.pow(translatedLast.y, 2),
    ]),
  );
  const scaledCurve = translatedCurve.map((point) => ({
    x: point.x / scale,
    y: point.y / scale,
  }));
  return subdivideCurve(scaledCurve);
};

// rotate around the origin
export const rotate = (curve: Point[], theta: number): Point[] => {
  return curve.map((point) => ({
    x: Math.cos(theta) * point.x - Math.sin(theta) * point.y,
    y: Math.sin(theta) * point.x + Math.cos(theta) * point.y,
  }));
};

// /**
//  *        c
//  *    - - - - - - -
//  *    - A      B-
//  *    -       -
//  * b  -     -
//  *    -   -    a
//  *    -C-
//  *    -
//  */
// // Returns angle C using law of cosines.
// const solveAngle = (a, b, c) => {
//   const radian = (a * a + b * b - c * c) / (2 * a * b);
//   if (radian >= -1 && radian < 1) {
//     return radianToDegree(Math.acos(radian));
//   } else if (radian <= 1) {
//     return radianToDegree(Math.sqrt((c * c - (a - b) * (a - b)) / (a * b)));
//   } else {
//     throw new Error('No solution');
//   }
// };
// // Returns side c using law of cosines.
// const solveSide = (a, b, C) => {
//   C = degreeToRadian(C);
//   if (C > 0) {
//     return Math.sqrt(a * a + b * b - 2 * a * b * Math.cos(C));
//   } else {
//     return Math.sqrt((a - b) * (a - b) + a * b * C * C * (1 - (C * C) / 12));
//   }
// };

// export const solveTriangle = ({ a, b, c, A, B, C } = {}) => {
//   const sides = [a, b, c].filter((x) => isNumber(x)).length;
//   const angles = [A, B, C].filter((x) => isNumber(x)).length;
//   let area, status;
//   let uniqResult = true;

//   if (sides + angles !== 3) {
//     throw new Error('Should exactly 3 input');
//   } else if (sides === 0) {
//     throw new Error('Need at least one side');
//   } else if (sides === 3) {
//     status = 'SSS';
//     if (c >= a + b || a >= b + c || b >= c + a) {
//       throw new Error(`${status} - No solution`);
//     }
//     A = solveAngle(b, c, a);
//     B = solveAngle(c, a, b);
//     C = solveAngle(a, b, c);
//     // Heron's formula
//     const s = (a + b + c) / 2;
//     area = Math.sqrt(s * (s - a) * (s - b) * (s - c));
//   } else if (angles === 2) {
//     if (
//       (!isNumber(A) && isNumber(a)) ||
//       (!isNumber(B) && isNumber(b)) ||
//       (!isNumber(C) && isNumber(c))
//     ) {
//       status = 'ASA';
//     } else {
//       status = 'AAS';
//     }
//     // Find missing angle
//     if (!isNumber(A)) {
//       A = 180 - B - C;
//     }
//     if (!isNumber(B)) {
//       B = 180 - C - A;
//     }
//     if (!isNumber(C)) {
//       C = 180 - A - B;
//     }
//     if (A <= 0 || B <= 0 || C <= 0) {
//       throw new Error(`${status} - No solution`);
//     }
//     const sinA = Math.sin(degreeToRadian(A));
//     const sinB = Math.sin(degreeToRadian(B));
//     const sinC = Math.sin(degreeToRadian(C));
//     // Use law of sines to find sides
//     let ratio; // side / sin(angle)
//     if (isNumber(a)) {
//       ratio = a / sinA;
//       area = (a * ratio * sinB * sinC) / 2;
//     }
//     if (isNumber(b)) {
//       ratio = b / sinB;
//       area = (b * ratio * sinC * sinA) / 2;
//     }
//     if (isNumber(c)) {
//       ratio = c / sinC;
//       area = (c * ratio * sinA * sinB) / 2;
//     }
//     if (!isNumber(a)) {
//       a = ratio * sinA;
//     }
//     if (!isNumber(b)) {
//       b = ratio * sinB;
//     }
//     if (!isNumber(c)) {
//       c = ratio * sinC;
//     }
//   } else if (
//     (isNumber(A) && !isNumber(a)) ||
//     (isNumber(B) && !isNumber(b)) ||
//     (isNumber(C) && !isNumber(c))
//   ) {
//     status = 'SAS';
//     if (
//       (isNumber(A) && A >= 180) ||
//       (isNumber(B) && B >= 180) ||
//       (isNumber(C) && C >= 180)
//     ) {
//       throw new Error(`${status} - No solution`);
//     }
//     if (!isNumber(a)) {
//       a = solveSide(b, c, A);
//     }
//     if (!isNumber(b)) {
//       b = solveSide(c, a, B);
//     }
//     if (!isNumber(c)) {
//       c = solveSide(a, b, C);
//     }
//     if (!isNumber(A)) {
//       A = solveAngle(b, c, a);
//     }
//     if (!isNumber(B)) {
//       B = solveAngle(c, a, b);
//     }
//     if (!isNumber(C)) {
//       C = solveAngle(a, b, c);
//     }
//     if (isNumber(A)) {
//       area = (b * c * Math.sin(degreeToRadian(A))) / 2;
//     }
//     if (isNumber(B)) {
//       area = (c * a * Math.sin(degreeToRadian(B))) / 2;
//     }
//     if (isNumber(C)) {
//       area = (a * b * Math.sin(degreeToRadian(C))) / 2;
//     }
//   } else {
//     status = 'SSA';
//     let knownSide, knownAngle, partialSide;
//     if (isNumber(a) && isNumber(A)) {
//       knownSide = a;
//       knownAngle = A;
//     }
//     if (isNumber(b) && isNumber(B)) {
//       knownSide = b;
//       knownAngle = B;
//     }
//     if (isNumber(c) && isNumber(C)) {
//       knownSide = c;
//       knownAngle = C;
//     }
//     if (isNumber(a) && !isNumber(A)) {
//       partialSide = a;
//     }
//     if (isNumber(b) && !isNumber(B)) {
//       partialSide = b;
//     }
//     if (isNumber(c) && !isNumber(C)) {
//       partialSide = c;
//     }
//     if (knownAngle >= 180) {
//       throw new Error(`${status} - No solution`);
//     }
//     const ratio = knownSide / Math.sin(degreeToRadian(knownAngle));
//     const temp = partialSide / ratio;
//     let partialAngle, unknownSide, unknownAngle;
//     if (temp > 1 || (knownAngle >= 90 && partialSide >= knownSide)) {
//       throw new Error(`${status} - No solution`);
//     } else if (temp === 1 || knownSide >= partialSide) {
//       status += ' - Unique solution';
//       partialAngle = radianToDegree(Math.asin(temp));
//       unknownAngle = 180 - knownAngle - partialAngle;
//       unknownSide = ratio * Math.sin(degreeToRadian(unknownAngle)); // Law of sines
//       area =
//         (knownSide * partialSide * Math.sin(degreeToRadian(unknownAngle))) / 2;
//     } else {
//       uniqResult = false;
//       status += ' - Two solutions';
//       const partialAngle0 = radianToDegree(Math.asin(temp));
//       const partialAngle1 = 180 - partialAngle0;
//       const unknownAngle0 = 180 - knownAngle - partialAngle0;
//       const unknownAngle1 = 180 - knownAngle - partialAngle1;
//       const unknownSide0 = ratio * Math.sin(degreeToRadian(unknownAngle0)); // Law of sines
//       const unknownSide1 = ratio * Math.sin(degreeToRadian(unknownAngle1)); // Law of sines
//       partialAngle = [partialAngle0, partialAngle1];
//       unknownAngle = [unknownAngle0, unknownAngle1];
//       unknownSide = [unknownSide0, unknownSide1];
//       area = [
//         (knownSide * partialSide * Math.sin(degreeToRadian(unknownAngle0))) / 2,
//         (knownSide * partialSide * Math.sin(degreeToRadian(unknownAngle1))) / 2,
//       ];
//     }
//     if (isNumber(a) && !isNumber(A)) {
//       A = partialAngle;
//     }
//     if (isNumber(b) && !isNumber(B)) {
//       B = partialAngle;
//     }
//     if (isNumber(c) && !isNumber(C)) {
//       C = partialAngle;
//     }
//     if (!isNumber(a) && !isNumber(A)) {
//       a = unknownSide;
//       A = unknownAngle;
//     }
//     if (!isNumber(b) && !isNumber(B)) {
//       b = unknownSide;
//       B = unknownAngle;
//     }
//     if (!isNumber(c) && !isNumber(C)) {
//       c = unknownSide;
//       C = unknownAngle;
//     }
//   }

//   return toPairs({ a, b, c, A, B, C, area }).reduce((acc, [k, v]) => {
//     (isArray(v) ? v : uniqResult ? [v] : [v, v]).forEach(
//       (x, i) =>
//         (acc[i] = {
//           ...acc[i],
//           [k]: x,
//         }),
//     );
//     return acc;
//   }, []);
// };

// /**
//  *        c
//  * p1 - - - - - - p2
//  *    - A     -
//  *  b -     -
//  *    -   - a
//  *    - -
//  * p3 -
//  * @returns {x , y} p3
//  */
// export const twoCoordinatesSASToLastCorrdinate = (p1, p2, b, A) => {
//   'worklet';
//   const { x: x1, y: y1 } = p1;
//   const { x: x2, y: y2 } = p2;
//   const c = distance(p1, p2);
//   const a1 = degreeToRadian(A);
//   const { x: u, y: v } = subtract(p2, p1);
//   const rhs1 = x1 * u + y1 * v + b * c * Math.cos(a1);
//   const rhs2 = y2 * u - x2 * v - b * c * Math.sin(a1);
//   return {
//     x: (1 / Math.pow(c, 2)) * (u * rhs1 - v * rhs2),
//     y: (1 / Math.pow(c, 2)) * (v * rhs1 + u * rhs2),
//   };
// };

// export const midPoint = (p1, p2) => {
//   'worklet';
//   return { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 };
// };
